import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/AuthContext";
import api, { errMsg } from "@/api";
import SiteNav from "@/components/SiteNav";
import { BookingDialog } from "@/components/BookingDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Video, Download, ExternalLink, BookOpen, CheckCircle2, Circle, ClipboardList, CalendarClock, Wallet, Trophy } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function PaymentBadge({ status, t }) {
  if (status === "paid")
    return <Badge className="bg-secondary text-secondary-foreground" data-testid="payment-status-paid">{t("dash.paid")}</Badge>;
  return <Badge className="bg-purple-100 text-purple-800 border border-purple-300" data-testid="payment-status-unpaid">{t("dash.unpaid")}</Badge>;
}

function StatusBadge({ status, t }) {
  const map = {
    pending: ["bg-amber-100 text-amber-900", t("dash.pendingApproval"), "booking-status-pending"],
    approved: ["bg-teal-100 text-teal-900", t("dash.approved"), "booking-status-approved"],
    declined: ["bg-red-100 text-red-800", t("dash.declined"), "booking-status-declined"],
    completed: ["bg-stone-200 text-stone-700", t("dash.completed"), "booking-status-completed"],
  };
  const [cls, label, tid] = map[status] || map.pending;
  return <Badge className={cls} data-testid={tid}>{label}</Badge>;
}

export default function StudentDashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [lessons, setLessons] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [progress, setProgress] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [bookOpen, setBookOpen] = useState(params.get("book") === "1");
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try {
      const [l, b, m, p, q] = await Promise.all([
        api.get("/my/lessons"), api.get("/my/bookings"), api.get("/my/materials"),
        api.get("/my/progress"), api.get("/my/quizzes"),
      ]);
      setLessons(l.data);
      setBookings(b.data);
      setMaterials(m.data);
      setProgress(p.data);
      setQuizzes(q.data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  useEffect(() => { load(); }, []);

  const upcoming = lessons.filter((l) => l.status === "scheduled");
  const history = lessons.filter((l) => l.status !== "scheduled");

  const downloadMaterial = async (mat) => {
    try {
      const res = await api.get(`/materials/${mat.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = mat.original_filename || mat.title;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const milestonesDone = progress?.milestones?.filter((m) => m.done).length || 0;
  const milestonesTotal = progress?.milestones?.length || 0;

  return (
    <div className="min-h-screen" data-testid="student-dashboard">
      <SiteNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold" data-testid="dashboard-greeting">
              {t("dash.hello")}, {user?.name?.split(" ")[0]}!
            </h1>
            {lang === "hu" && <p className="text-sm text-muted-foreground mt-1 italic">{t("common.langNote")}</p>}
          </div>
          <Button onClick={() => setBookOpen(true)} data-testid="book-lesson-btn">{t("dash.bookMore")}</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="flex-wrap h-auto" data-testid="student-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">{t("dash.overview")}</TabsTrigger>
            <TabsTrigger value="lessons" data-testid="tab-lessons">{t("dash.lessons")}</TabsTrigger>
            <TabsTrigger value="materials" data-testid="tab-materials">{t("dash.materials")}</TabsTrigger>
            <TabsTrigger value="progress" data-testid="tab-progress">{t("dash.progress")}</TabsTrigger>
            <TabsTrigger value="quizzes" data-testid="tab-quizzes">{t("dash.quizzes")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid sm:grid-cols-3 gap-4">
              <Card><CardContent className="p-5 flex items-center gap-4">
                <CalendarClock className="w-8 h-8 text-teal-600" />
                <div><p className="text-2xl font-serif font-bold" data-testid="stat-upcoming">{upcoming.length}</p><p className="text-sm text-muted-foreground">{t("dash.upcoming")}</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-5 flex items-center gap-4">
                <BookOpen className="w-8 h-8 text-primary" />
                <div><p className="text-2xl font-serif font-bold" data-testid="stat-remaining">{progress?.remaining_lessons ?? 0}</p><p className="text-sm text-muted-foreground">{t("dash.remaining")}</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-5 flex items-center gap-4">
                <Trophy className="w-8 h-8 text-amber-600" />
                <div><p className="text-2xl font-serif font-bold" data-testid="stat-milestones">{milestonesDone}/{milestonesTotal}</p><p className="text-sm text-muted-foreground">{t("dash.milestones")}</p></div>
              </CardContent></Card>
            </div>

            <Card data-testid="upcoming-card">
              <CardContent className="p-6">
                <h2 className="font-serif font-semibold text-lg">{t("dash.upcoming")}</h2>
                {upcoming.length === 0 && <p className="mt-3 text-muted-foreground">{t("dash.noUpcoming")}</p>}
                <div className="mt-3 space-y-3">
                  {upcoming.map((l) => (
                    <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-4" data-testid={`upcoming-lesson-${l.id}`}>
                      <div>
                        <p className="font-semibold">{fmtDate(l.date)}</p>
                        <p className="text-sm text-muted-foreground">{l.topic || `${l.duration_min} min`}</p>
                      </div>
                      {l.teams_link && (
                        <Button size="sm" variant="outline" className="text-teal-700 border-teal-300" onClick={() => window.open(l.teams_link, "_blank")} data-testid="join-teams-lesson-btn">
                          <Video className="w-4 h-4 mr-1.5" /> {t("dash.joinTeams")}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="payments-card">
              <CardContent className="p-6">
                <h2 className="font-serif font-semibold text-lg flex items-center gap-2"><Wallet className="w-5 h-5 text-purple-700" /> {t("dash.payments")}</h2>
                <div className="mt-3 space-y-3">
                  {bookings.length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
                  {bookings.map((b) => (
                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg p-4" data-testid={`booking-row-${b.id}`}>
                      <div>
                        <p className="font-semibold">{b.package_label} — ${b.price}</p>
                        <p className="text-sm text-muted-foreground">{t("admin.requested")}: {fmtDate(b.created_at)}</p>
                        {b.status === "approved" && b.payment_status !== "paid" && (
                          <p className="mt-1 text-xs text-purple-800" data-testid="zelle-instructions">
                            Zelle: <strong>englishuniversestars@yahoo.com</strong> · {t("booking.zelleMemo")}: {b.name} — {b.package_label}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2"><StatusBadge status={b.status} t={t} /><PaymentBadge status={b.payment_status} t={t} /></div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="lessons" className="mt-6">
            <Card>
              <CardContent className="p-6 space-y-3">
                <h2 className="font-serif font-semibold text-lg">{t("dash.history")}</h2>
                {history.length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
                {history.map((l) => (
                  <div key={l.id} className="border border-border rounded-lg p-4" data-testid={`history-lesson-${l.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold">{fmtDate(l.date)}</p>
                      <Badge variant="secondary" data-testid={`lesson-status-${l.id}`}>{l.status === "completed" ? t("dash.completed") : t(`dash.${l.status}`)}</Badge>
                    </div>
                    {l.topic && <p className="mt-1 text-sm"><span className="text-muted-foreground">{t("dash.topic")}:</span> {l.topic}</p>}
                    {l.notes && <p className="mt-1 text-sm"><span className="text-muted-foreground">{t("dash.notes")}:</span> {l.notes}</p>}
                    {l.homework && <p className="mt-1 text-sm" data-testid={`lesson-homework-${l.id}`}><span className="text-muted-foreground">{t("dash.homework")}:</span> {l.homework}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="materials" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-4" data-testid="materials-grid">
              {materials.length === 0 && <p className="text-muted-foreground">{t("dash.noMaterials")}</p>}
              {materials.map((m) => (
                <Card key={m.id} data-testid={`material-card-${m.id}`}>
                  <CardContent className="p-5">
                    <p className="font-serif font-semibold">{lang === "hu" && m.title_hu ? m.title_hu : m.title}</p>
                    {m.description && <p className="mt-1 text-sm text-muted-foreground">{m.description}</p>}
                    <div className="mt-4">
                      {m.type === "file" ? (
                        <Button size="sm" variant="outline" onClick={() => downloadMaterial(m)} data-testid={`material-download-${m.id}`}>
                          <Download className="w-4 h-4 mr-1.5" /> {t("dash.download")}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => window.open(m.external_url, "_blank")} data-testid={`material-link-${m.id}`}>
                          <ExternalLink className="w-4 h-4 mr-1.5" /> {t("dash.openLink")}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="progress" className="mt-6 space-y-6" data-testid="progress-tab">
            <div className="grid sm:grid-cols-3 gap-4">
              <Card><CardContent className="p-5">
                <p className="text-sm text-muted-foreground">{t("dash.level")}</p>
                <p className="mt-1 text-xl font-serif font-bold capitalize" data-testid="progress-level">{t(`levels.items.${progress?.level || "beginner"}`)}</p>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <p className="text-sm text-muted-foreground">{t("dash.attendance")}</p>
                <p className="mt-1 text-xl font-serif font-bold" data-testid="progress-attendance">{progress?.attendance != null ? `${progress.attendance}%` : "—"}</p>
              </CardContent></Card>
              <Card><CardContent className="p-5">
                <p className="text-sm text-muted-foreground">{t("admin.completedLessons")}</p>
                <p className="mt-1 text-xl font-serif font-bold" data-testid="progress-completed">{progress?.lessons_completed ?? 0}</p>
              </CardContent></Card>
            </div>

            <Card data-testid="milestones-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="font-serif font-semibold text-lg">{t("dash.milestones")}</h2>
                  <span className="text-sm font-bold text-amber-700">{milestonesTotal ? Math.round(milestonesDone / milestonesTotal * 100) : 0}%</span>
                </div>
                <Progress value={milestonesTotal ? (milestonesDone / milestonesTotal) * 100 : 0} className="mt-3" data-testid="milestone-progress" />
                <ul className="mt-4 space-y-2.5">
                  {(progress?.milestones || []).map((m) => (
                    <li key={m.id} className="flex items-center gap-3" data-testid={`milestone-${m.id}`}>
                      {m.done ? <CheckCircle2 className="w-5 h-5 text-amber-600 shrink-0" /> : <Circle className="w-5 h-5 text-stone-300 shrink-0" />}
                      <span className={m.done ? "line-through text-muted-foreground" : ""}>
                        {lang === "hu" && m.title_hu ? m.title_hu : m.title}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h2 className="font-serif font-semibold text-lg flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" /> {t("dash.homework")}</h2>
                  <ul className="mt-3 space-y-2.5">
                    {(progress?.homework || []).length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
                    {(progress?.homework || []).map((h) => (
                      <li key={h.lesson_id} className="border-l-2 border-amber-500 pl-3 text-sm">
                        <span className="text-muted-foreground">{fmtDate(h.date)}:</span> {h.homework}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h2 className="font-serif font-semibold text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-600" /> {t("dash.quizScores")}</h2>
                  <ul className="mt-3 space-y-2.5">
                    {(progress?.quiz_attempts || []).length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
                    {(progress?.quiz_attempts || []).map((a) => (
                      <li key={a.id} className="flex items-center justify-between text-sm border-b border-border pb-2" data-testid={`quiz-attempt-${a.id}`}>
                        <span>{a.quiz_title}</span>
                        <Badge className={a.score / Math.max(a.total, 1) >= 0.7 ? "bg-secondary text-secondary-foreground" : "bg-amber-100 text-amber-900"}>
                          {a.score}/{a.total}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="quizzes" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-4" data-testid="quizzes-grid">
              {quizzes.length === 0 && <p className="text-muted-foreground">{t("dash.noQuizzes")}</p>}
              {quizzes.map((q) => (
                <Card key={q.id} data-testid={`quiz-card-${q.id}`}>
                  <CardContent className="p-5">
                    <p className="font-serif font-semibold">{lang === "hu" && q.title_hu ? q.title_hu : q.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{q.question_count} {t("dash.questions")}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm">
                        {q.best_score != null ? <>{t("dash.bestScore")}: <strong>{q.best_score}%</strong></> : "—"}
                      </span>
                      <Button size="sm" onClick={() => navigate(`/quiz/${q.id}`)} data-testid={`take-quiz-${q.id}`}>{t("dash.takeQuiz")}</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} onCreated={load} />
    </div>
  );
}
